/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import {
  View,
  NativeSyntheticEvent,
  NativeScrollEvent,
  Platform,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

import TextView from '@airasia-phoenix/react-native/components/TextView';
import { getTextStyle, pd } from '@airasia-phoenix/react-native/utils/utils';
import HorizontalContentCardGroupView from '@airasia-phoenix/react-native/widgets/HorizontalContentCardGroupView';

import { useAppConfig } from '@airasia-rn/ui';
import { useLogger } from '@airasia-rn/core-logger';

import { useSnapRecentSearches } from '../../hooks/useSnapRecentSearches';
import type { SnapRecentSearchEntry } from '../../types';
import { SnapSearchWidgetEvent } from '../../analytics';
import { EVENT_NAMES } from '../../analytics/snapSearchWidget/snapSearchWidget.meta';
import { useCarouselInfiniteScroll } from './useCarouselInfiniteScroll';
import { buildPrimaryText, buildSecondaryText, buildGuestText } from './utils';
import type { RecentSearchCardItemProps } from './types';
import Constants from './constants';
import type { RecentSearchCardItem } from './types';

interface RecentSearchesCarouselViewProps {
  onCardPress?: (entry: SnapRecentSearchEntry, index: number) => void;
  maxCount?: number;
}

type WebMouseDragTarget = {
  scrollLeft: number;
  style?: { cursor?: string };
};

type WebMouseDragEvent = {
  clientX: number;
  currentTarget: WebMouseDragTarget;
  preventDefault: () => void;
};

type WebClickEvent = {
  preventDefault: () => void;
  stopPropagation: () => void;
};

const MOUSE_DRAG_CLICK_THRESHOLD = 5;

export type RecentSearchMouseDragState = {
  isDragging: boolean;
  didDrag: boolean;
  startClientX: number;
  startScrollLeft: number;
};

export const handleRecentSearchMouseDown = (
  state: RecentSearchMouseDragState,
  event: WebMouseDragEvent,
) => {
  state.isDragging = true;
  state.didDrag = false;
  state.startClientX = event.clientX;
  state.startScrollLeft = event.currentTarget.scrollLeft;
  if (event.currentTarget.style) event.currentTarget.style.cursor = 'grabbing';
  event.preventDefault();
};

export const handleRecentSearchMouseMove = (
  state: RecentSearchMouseDragState,
  event: WebMouseDragEvent,
) => {
  if (!state.isDragging) return;

  if (
    Math.abs(event.clientX - state.startClientX) >= MOUSE_DRAG_CLICK_THRESHOLD
  ) {
    state.didDrag = true;
  }
  event.preventDefault();
  event.currentTarget.scrollLeft =
    state.startScrollLeft - (event.clientX - state.startClientX);
};

export const handleRecentSearchMouseUp = (
  state: RecentSearchMouseDragState,
  target?: WebMouseDragTarget,
) => {
  state.isDragging = false;
  if (target?.style) target.style.cursor = 'grab';
};

export const handleRecentSearchClickCapture = (
  state: RecentSearchMouseDragState,
  event: WebClickEvent,
) => {
  if (!state.didDrag) return;

  event.preventDefault();
  event.stopPropagation();
  state.didDrag = false;
};

const RecentSearchesCarouselView = ({
  onCardPress,
  maxCount = 5, // Default to 5 if not provided
}: RecentSearchesCarouselViewProps) => {
  const { t } = useTranslation('snap');
  const { recentSearchesStore, isLoading } = useSnapRecentSearches();
  const { config: appConfig } = useAppConfig();
  const { logEvent } = useLogger();
  const hasTrackedViewRef = useRef(false);
  const mouseDragStateRef = useRef<RecentSearchMouseDragState>({
    isDragging: false,
    didDrag: false,
    startClientX: 0,
    startScrollLeft: 0,
  });

  const [carouselData, setCarouselData] = useState<RecentSearchCardItem[]>([]);

  // Locale-aware date formatter for the card secondary text (Scenario 8)
  const formatDateForCard = useCallback((dateStr: string): string => {
    try {
      return format(new Date(dateStr), 'EEE, d MMM');
    } catch {
      return dateStr;
    }
  }, []);

  /**
   * Map SnapRecentSearchEntry[] → RecentSearchCardItem[]
   */
  const mapToCards = useCallback(
    (entries: SnapRecentSearchEntry[]): RecentSearchCardItem[] =>
      entries.map((entry) => {
        // Display original (possibly expired) dates on the card.
        // Date resolution (fallback to defaults) happens only on navigation
        // inside useRecentSearchNavigation when the card is pressed.
        const routeId = `${entry.from?.StationCode}-${entry.to?.StationCode}`;

        return {
          onPress: () => {
            const entryIndex = entries.indexOf(entry);
            // SNAP unified event: aa_snp_lastsearched_clicked
            const flightRoute = `${entry.from.StationCode}-${entry.to.StationCode}`;
            const clickedParams =
              SnapSearchWidgetEvent.snapLastSearchedClickedEvent({
                flightRoute,
                position: entryIndex + 1,
              });
            logEvent(EVENT_NAMES.LAST_SEARCHED_CLICKED, clickedParams);
            onCardPress?.(entry, entryIndex);
          },
          _routeId: routeId,
          model: entry,
          hasTopIcon: false,
          imageHeight: Constants.IMAGE_HEIGHT,
          imageWidth: Constants.IMAGE_WIDTH,
          width: Constants.CARD_WIDTH,
          hasOutlineBorder: true,
          imageProps: {
            image: Constants.DEST_IMAGE_URL.replace(
              '{0}',
              entry.to.StationCode?.toLowerCase() ?? '',
            ),
            cacheProps: {
              priority: 'normal',
              cachePolicy: 'immutable',
            },
            onError: () => {
              setCarouselData((curr) =>
                curr.map((item) =>
                  item._routeId === routeId
                    ? {
                        ...item,
                        imageProps: {
                          image: Constants.PLACEHOLDER_IMAGE,
                          height: Constants.IMAGE_HEIGHT,
                          width: Constants.IMAGE_WIDTH,
                        },
                      }
                    : item,
                ),
              );
            },
            height: Constants.IMAGE_HEIGHT,
            width: Constants.IMAGE_WIDTH,
          },
          contentDetailsPropsValues: {
            primaryText: buildPrimaryText(entry, Constants.PRIMARY_TEXT),
            secondaryText: buildSecondaryText(entry, formatDateForCard),
            tertiaryInfo: {
              iconAndText: {
                icon: {
                  icon: pd.icon.main.social.people_outline,
                },
                text: buildGuestText(entry.rooms, (key, opts) =>
                  String(t(key, opts as Record<string, unknown>)),
                ),
              },
            },
            width: Constants.CARD_WIDTH,
            imageWidth: Constants.IMAGE_WIDTH,
          },
        };
      }),
    [onCardPress, formatDateForCard, t, appConfig.currency, logEvent],
  );

  const allItems = useMemo(
    () => (recentSearchesStore?.recentSearches ?? []).slice(0, maxCount),
    [recentSearchesStore?.recentSearches, maxCount],
  );

  const { handleScroll, loadedCountRef, reset } =
    useCarouselInfiniteScroll<SnapRecentSearchEntry>({
      allItems,
      loadCount: Constants.SUBSEQUENT_LOAD_COUNT,
      onLoadMore: (items: SnapRecentSearchEntry[]) => {
        setCarouselData((prev) => [...prev, ...mapToCards(items)]);
      },
    });

  const handleCarouselScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      handleScroll(event);
    },
    [handleScroll],
  );

  useEffect(() => {
    if (isLoading || !allItems.length) return;

    reset();

    const initialItems = allItems.slice(0, Constants.INITIAL_LOAD_COUNT);
    setCarouselData(mapToCards(initialItems));
    loadedCountRef.current = initialItems.length;
  }, [isLoading, allItems, mapToCards, reset, loadedCountRef]);

  // Event 1 (app): view_item_list when carousel first renders with items
  useEffect(() => {
    if (hasTrackedViewRef.current || !allItems.length) return;
    hasTrackedViewRef.current = true;

    // SNAP unified event: aa_snp_lastsearched_viewed for each item
    allItems.forEach((item, index) => {
      const flightRoute = `${item.from.StationCode}-${item.to.StationCode}`;
      const viewedParams = SnapSearchWidgetEvent.snapLastSearchedViewedEvent({
        flightRoute,
        position: index + 1,
      });
      logEvent(EVENT_NAMES.LAST_SEARCHED_VIEWED, viewedParams);
    });
  }, [allItems, appConfig.currency, logEvent]);

  // Scenario 1: New user – don't render anything (title included)
  if (isLoading || !allItems.length) return null;

  return (
    <View style={styles.container}>
      <View style={styles.titleWrapper}>
        <TextView
          text={t('searchSnapPage.cardRecentSearch.title', {
            defaultValue: 'Continue your search',
          })}
          style={[
            getTextStyle(
              pd.sys.typescale.body_text_medium,
              pd.sys.colour.content_primary,
            ),
          ]}
        />
      </View>

      <View style={styles.carouselContainer}>
        <HorizontalContentCardGroupView
          orientation={Constants.CAROUSEL_ORIENTATION}
          contentCardProps={
            carouselData as unknown as RecentSearchCardItemProps[]
          }
          viewHeight={Constants.CAROUSEL_HEIGHT}
          flatListProps={{
            decelerationRate: Constants.SCROLL_SPEED,
            showsHorizontalScrollIndicator: false,
            onScroll: handleCarouselScroll,
            scrollEventThrottle: 16,
            // @ts-expect-error React Native Web supports DOM mouse handlers,
            // but FlatListProps exposes only the cross-platform event surface.
            onMouseDown:
              Platform.OS === 'web'
                ? (event: WebMouseDragEvent) =>
                    handleRecentSearchMouseDown(
                      mouseDragStateRef.current,
                      event,
                    )
                : undefined,
            onMouseMove:
              Platform.OS === 'web'
                ? (event: WebMouseDragEvent) =>
                    handleRecentSearchMouseMove(
                      mouseDragStateRef.current,
                      event,
                    )
                : undefined,
            onMouseUp:
              Platform.OS === 'web'
                ? (event: WebMouseDragEvent) =>
                    handleRecentSearchMouseUp(
                      mouseDragStateRef.current,
                      event.currentTarget,
                    )
                : undefined,
            onMouseLeave:
              Platform.OS === 'web'
                ? (event: WebMouseDragEvent) =>
                    handleRecentSearchMouseUp(
                      mouseDragStateRef.current,
                      event.currentTarget,
                    )
                : undefined,
            onClickCapture:
              Platform.OS === 'web'
                ? (event: WebClickEvent) =>
                    handleRecentSearchClickCapture(
                      mouseDragStateRef.current,
                      event,
                    )
                : undefined,
          }}
        />
      </View>
    </View>
  );
};

export default RecentSearchesCarouselView;

const styles = StyleSheet.create({
  container: {
    paddingLeft: pd.ref.space.spacing40.px,
    paddingBottom: pd.ref.space.spacing30.px,
  },
  titleWrapper: {
    marginBottom: pd.ref.space.spacing20.px,
  },
  carouselContainer: {
    marginLeft: -pd.ref.space.spacing10.px,
  },
});
